/**
 * Monthly leave + attendance summary for one employee (employee portal).
 */
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { APP_BRANCH_VALUE_TO_ATTENDANCE_LABEL } from "../config/appBranches";
import { branchRowForLabel } from "../config/attendanceBranches";
import {
  buildEmployeeCashLeaveLabelsByEmployee,
  buildEmployeeCashLeaveYmdByEmployee,
  buildHolidayYmdSetFromDocs,
  cutoffDateForSelectedMonth,
  employeeCashLeaveLabelForDay,
  employeeCashLeaveYmdSet,
  getWorkingDaysTillCutoff,
  loadMonthlyAttendanceRows,
} from "./payrollAttendanceCore";
import {
  MONTHLY_PAID_LEAVE_ALLOWANCE,
  paidLeaveOverAllowance,
  paidLeaveRemaining,
} from "../config/employeeLeavePolicy";

function formatYmdLabel(ymdKey) {
  const m = String(ymdKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymdKey || "—";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function isInCalendarMonth(ymdKey, calendarYear, monthIndex) {
  const parts = String(ymdKey || "").split("-").map(Number);
  if (parts.length < 3) return false;
  return parts[0] === calendarYear && parts[1] === monthIndex + 1;
}

/**
 * @returns {Promise<{
 *   monthLabel: string,
 *   workingDaysInRange: number,
 *   paidLeaveCount: number,
 *   paidLeaveAllowance: number,
 *   paidLeaveRemaining: number,
 *   paidLeaveOverAllowance: number,
 *   paidLeaveDates: { date: string, dateLabel: string, label: string }[],
 *   presentDays: number | null,
 *   absentDays: number | null,
 *   absentDates: string[],
 *   halfDayDates: string[],
 *   halfDayCount: number,
 *   attendanceError?: string
 * }>}
 */
export async function fetchEmployeeLeaveSnapshot(branchFilter, calendarYear, monthIndex, employeeId) {
  const monthDate = new Date(calendarYear, monthIndex, 1);
  const monthLabel = monthDate.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const eid = String(employeeId || "").trim();
  if (!eid) {
    return {
      monthLabel,
      workingDaysInRange: 0,
      paidLeaveCount: 0,
      paidLeaveAllowance: MONTHLY_PAID_LEAVE_ALLOWANCE,
      paidLeaveRemaining: MONTHLY_PAID_LEAVE_ALLOWANCE,
      paidLeaveOverAllowance: 0,
      paidLeaveDates: [],
      presentDays: null,
      absentDays: null,
      absentDates: [],
      halfDayDates: [],
      halfDayCount: 0,
    };
  }

  const attendanceLabel = APP_BRANCH_VALUE_TO_ATTENDANCE_LABEL[branchFilter] || "Main Shop";
  const shop = branchRowForLabel(attendanceLabel);

  const holidaySnap = await getDocs(collection(db, "holidays")).catch(() => ({ docs: [] }));
  const monthStart = new Date(calendarYear, monthIndex, 1);
  const cutoffDt = cutoffDateForSelectedMonth(monthDate);
  const holidaySet = buildHolidayYmdSetFromDocs(holidaySnap.docs, monthStart, cutoffDt);
  const workingDays = getWorkingDaysTillCutoff(monthDate, holidaySet);
  const cashLeaveByEmp = buildEmployeeCashLeaveYmdByEmployee(holidaySnap.docs, monthStart, cutoffDt);
  const leaveLabelsByEmp = buildEmployeeCashLeaveLabelsByEmployee(holidaySnap.docs, monthStart, cutoffDt);

  const cashLeaveSet = employeeCashLeaveYmdSet(cashLeaveByEmp, eid);
  const paidLeaveDates = [...cashLeaveSet]
    .filter((d) => isInCalendarMonth(d, calendarYear, monthIndex))
    .sort()
    .map((date) => ({
      date,
      dateLabel: formatYmdLabel(date),
      label: employeeCashLeaveLabelForDay(leaveLabelsByEmp, eid, date),
    }));

  let presentDays = null;
  let absentDays = null;
  let absentDates = [];
  let halfDayDates = [];
  let attendanceError;

  try {
    const pack = await loadMonthlyAttendanceRows(shop, monthDate);
    const row =
      pack.rows.find((r) => String(r.employeeId || "").trim().toLowerCase() === eid.toLowerCase()) ||
      null;
    if (row) {
      presentDays = Number(row.presentDays) || 0;
      absentDates = (row.absentDates || []).map(formatYmdLabel);
      absentDays = absentDates.length;
      halfDayDates = (row.halfDayDates || []).map(formatYmdLabel);
    }
  } catch (e) {
    console.warn("[employeeLeaveSnapshot] attendance load failed", e);
    attendanceError = e?.message || "Could not load attendance";
  }

  return {
    monthLabel,
    workingDaysInRange: workingDays.length,
    paidLeaveCount: paidLeaveDates.length,
    paidLeaveAllowance: MONTHLY_PAID_LEAVE_ALLOWANCE,
    paidLeaveRemaining: paidLeaveRemaining(paidLeaveDates.length),
    paidLeaveOverAllowance: paidLeaveOverAllowance(paidLeaveDates.length),
    paidLeaveDates,
    presentDays,
    absentDays,
    absentDates,
    halfDayDates,
    halfDayCount: halfDayDates.length,
    attendanceError,
  };
}
